import _inject from "@stylexjs/stylex/lib/stylex-inject";
var _inject2 = _inject;
import * as stylex from "@stylexjs/stylex";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarX, Orbit } from "lucide-react";
import { Button } from "@/components/button";
import { CardHeading } from "@/components/shared/card-heading";
import { EmptyState } from "@/components/shared/empty-state";
import { StatusAlert } from "@/components/shared/status-alert";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { SkeletonTable } from "@/components/ui/skeleton";
import { formatWeekendRange } from "@/core/guild-war/weekend-dates";
import { guildWarApi } from "@/lib/guild-war-api";
import { type WeekState } from "@/lib/guild-war-constants";
import { guildWarKeys } from "@/lib/query-keys";
import { usePendingTransitionWeekId, useTransitionMessage, type WeeksUiStore } from "@/lib/stores/weeks-ui-store";
type RenderableWeek = {
  id: string;
  state: WeekState;
  weekStartAt: string;
  weekEndAt: string;
  createdAt: string;
  updatedAt: string;
};
const nextWeekState: Readonly<Record<WeekState, WeekState | null>> = {
  draft: "signup_open",
  signup_open: "planning",
  planning: "completed",
  completed: null
};
type StatusTone = "default" | "active" | "muted" | "danger";
const getStateTone = (state: WeekState): StatusTone => state === "signup_open" ? "active" : "muted";
const formatWindow = (weekStartAt: string | Date, weekEndAt: string | Date) => `${new Date(weekStartAt).toLocaleString()} - ${new Date(weekEndAt).toLocaleString()}`;
const renderText = (value: unknown) => {
  if (value instanceof Date) return value.toISOString();
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return String(value);
  if (typeof value === "symbol") return value.toString();
  if (typeof value === "function") return value.name ? `[Function ${value.name}]` : "[Function]";
  try {
    return JSON.stringify(value);
  } catch {
    return Object.prototype.toString.call(value);
  }
};
type ExistingWeeksTableProps = {
  weeks: RenderableWeek[];
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => void;
  store: WeeksUiStore;
};
export function ExistingWeeksTable({
  weeks,
  isLoading,
  isError,
  onRefetch,
  store
}: ExistingWeeksTableProps) {
  const queryClient = useQueryClient();
  const pendingWeekId = usePendingTransitionWeekId(store);
  const transitionMessage = useTransitionMessage(store);
  const transitionWeekMutation = useMutation({
    mutationFn: ({
      weekId,
      toState
    }: {
      weekId: string;
      toState: WeekState;
    }) => guildWarApi.transitionWeek(weekId, toState),
    onMutate: ({
      weekId
    }) => {
      store.getState().setPendingTransitionWeekId(weekId);
      store.getState().setTransitionMessage(null);
    },
    onSuccess: async () => {
      store.getState().setPendingTransitionWeekId(null);
      await queryClient.invalidateQueries({
        queryKey: guildWarKeys.weeks()
      });
    },
    onError: error => {
      store.getState().setPendingTransitionWeekId(null);
      store.getState().setTransitionMessage({
        tone: "error",
        text: error instanceof Error ? error.message : "Failed to transition week state"
      });
    }
  });
  return <Card>
      <CardHeader>
        <CardHeading icon={<Orbit className="size-5 text-primary" />}>
          Existing Weeks
        </CardHeading>
        <CardDescription>
          Review the current lifecycle at a glance and move each week to its
          next valid state.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <SkeletonTable rows={4} columns={4} /> : null}
        {isError ? <StatusAlert tone="danger">
            Failed to load weeks.
            <Button variant="ghost" size="sm" className="ml-2" onClick={onRefetch}>
              Retry
            </Button>
          </StatusAlert> : null}

        {weeks.length ? <div className="overflow-x-auto">
            <table className="data-table text-left text-sm">
              <caption className="sr-only">
                Existing guild war weeks with current state and transition
                actions
              </caption>
              <thead>
                <tr>
                  <th scope="col">Week</th>
                  <th scope="col">State</th>
                  <th scope="col">Week Window</th>
                  <th scope="col">Action</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map(week => {
              const nextState = nextWeekState[week.state];
              const isThisRowPending = pendingWeekId === week.id;
              return <tr key={week.id}>
                      <th scope="row" className="max-w-56 truncate font-medium">
                        {renderText(formatWeekendRange({
                    weekStartAt: week.weekStartAt
                  }))}
                      </th>
                      <td>
                        <span {...stylex.props(styles.statusPill, statusToneStyles[getStateTone(week.state)])}>
                          {renderText(week.state).replaceAll("_", " ")}
                        </span>
                      </td>
                      <td className="text-muted-foreground">
                        {formatWindow(week.weekStartAt, week.weekEndAt)}
                      </td>
                      <td>
                        {nextState ? <Button size="sm" variant="secondary" disabled={isThisRowPending} onClick={() => {
                    transitionWeekMutation.mutate({
                      weekId: week.id,
                      toState: nextState
                    });
                  }}>
                            {isThisRowPending ? "Moving..." : `Move to ${renderText(nextState)}`}
                          </Button> : <span className="text-muted-foreground">
                            Complete
                          </span>}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div> : null}
        {!isLoading && !isError && !weeks.length ? <EmptyState icon={<CalendarX className="size-5" />} title="No weeks created" description="Create your first guild war week to get started." /> : null}
        {transitionMessage ? <p className="text-sm text-destructive">{transitionMessage.text}</p> : null}
      </CardContent>
    </Card>;
}
_inject2({
  ltr: ".x3nfvp2{display:inline-flex}",
  priority: 3000
});
_inject2({
  ltr: ".x6s0dn4{align-items:center}",
  priority: 3000
});
_inject2({
  ltr: ".x1neeqzj{gap:.4rem}",
  priority: 2000
});
_inject2({
  ltr: ".x1e6avla{border-radius:999px}",
  priority: 2000
});
_inject2({
  ltr: ".x1267ecw{padding-block:.35rem}",
  priority: 2000
});
_inject2({
  ltr: ".x1j9iwxf{padding-inline:.7rem}",
  priority: 2000
});
_inject2({
  ltr: ".x142gn8v{font-size:.74rem}",
  priority: 3000
});
_inject2({
  ltr: ".x1xlr1w8{font-weight:700}",
  priority: 3000
});
_inject2({
  ltr: ".x3gc2hl{letter-spacing:.03em}",
  priority: 3000
});
_inject2({
  ltr: ".xn80e1m{text-transform:capitalize}",
  priority: 3000
});
_inject2({
  ltr: ".x1tqc3zq{background-color:color-mix(in oklch,var(--surface-3) 90%,var(--secondary))}",
  priority: 3000
});
_inject2({
  ltr: ".x1k16nxv{color:color-mix(in oklch,var(--foreground) 85%,var(--primary))}",
  priority: 3000
});
_inject2({
  ltr: ".xmkeg23{border-width:1px}",
  priority: 2000
});
_inject2({
  ltr: ".x1y0btm7{border-style:solid}",
  priority: 2000
});
_inject2({
  ltr: ".x1x06y58{border-color:var(--surface-border)}",
  priority: 2000
});
_inject2({
  ltr: ".xwhnkoi{background-color:color-mix(in oklch,var(--accent) 20%,var(--surface-2))}",
  priority: 3000
});
_inject2({
  ltr: ".x1i1xfhz{background-color:color-mix(in oklch,var(--muted) 88%,var(--surface-2))}",
  priority: 3000
});
_inject2({
  ltr: ".x1vbqtx3{background-color:color-mix(in oklch,var(--destructive) 14%,var(--surface-2))}",
  priority: 3000
});
_inject2({
  ltr: ".x12cvf6i{color:color-mix(in oklch,var(--destructive) 78%,var(--foreground))}",
  priority: 3000
});
const styles = {
  statusPill: {
    k1xSpc: "x3nfvp2",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1neeqzj",
    kaIpWk: "x1e6avla",
    k8WAf4: "x1267ecw",
    kg3NbH: "x1j9iwxf",
    kGuDYH: "x142gn8v",
    k63SB2: "x1xlr1w8",
    kb6lSQ: "x3gc2hl",
    kP9fke: "xn80e1m",
    kWkggS: "x1tqc3zq",
    kMwMTN: "x1k16nxv",
    kMzoRj: "xmkeg23",
    ksu8eU: "x1y0btm7",
    kVAM5u: "x1x06y58",
    $$css: true
  },
  active: {
    kWkggS: "xwhnkoi",
    $$css: true
  },
  muted: {
    kWkggS: "x1i1xfhz",
    $$css: true
  },
  danger: {
    kWkggS: "x1vbqtx3",
    kMwMTN: "x12cvf6i",
    $$css: true
  }
};
const statusToneStyles = {
  default: null,
  active: styles.active,
  muted: styles.muted,
  danger: styles.danger
} satisfies Record<StatusTone, stylex.StyleXStyles>;